'use client'
import type { Data } from '@repo/strapi'
import { useTranslations } from 'next-intl'
import { useRef } from 'react'
import { Tag } from '@/components/elementary/Tag'
import { Tooltip } from '@/components/elementary/Tooltip'
import { ChevronDown } from '@/components/icons/ChevronDown'
import { Info } from '@/components/icons/Info'
import { FeatureCell } from '@/components/page-builder/global/pricingV1/featureComparison/FeatureCell'
import { PlanCallToAction } from '@/components/page-builder/global/pricingV1/shared/PlanCallToAction'
import { getColsClassName } from '@/components/page-builder/global/pricingV1/utils/table-helper'
import Heading from '@/components/typography/Heading'
import { Paragraph } from '@/components/typography/Paragraph'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import {
	pricingSections,
	STICKY_HEADER_FADE_DISTANCE,
	STICKY_HEADER_FADE_THRESHOLD,
} from '@/constants/pricing.constants'
import { useScrollFadeOut } from '@/hooks/useScrollFadeOut'
import { cn } from '@/lib/styles'
import { PRICING_TEST_IDS } from '@/lib/test-ids'

export function FeatureComparisonTable({
	data,
	currentTab,
	yearly,
	className,
}: {
	data?: Data.Component<'shared.pricing-plan-detail'>[] | null
	currentTab?: string | null
	yearly?: boolean
	className?: string
}) {
	const t = useTranslations('pricing')
	const tPlan = useTranslations('pricingPlanFeatures')
	const tSections = useTranslations('pricingPlanSections')

	const stickyRef = useRef<HTMLDivElement | null>(null)
	const containerRef = useRef<HTMLDivElement | null>(null)
	const opacity = useScrollFadeOut(
		containerRef,
		STICKY_HEADER_FADE_THRESHOLD,
		STICKY_HEADER_FADE_DISTANCE,
	)

	const gridColsClassName = currentTab
		? 'grid-cols-2'
		: getColsClassName(data?.length, { includeExtraColumn: true })
	const rowClassName = cn('grid divide-x divide-neutral-700', gridColsClassName)

	const plansData = currentTab ? data?.filter((item) => item.title === currentTab) : data
	return (
		<div
			ref={containerRef}
			className={cn('text-neutral-light-100', className)}
			style={{ opacity }}
			data-test-id={PRICING_TEST_IDS.COMPARE_CONTENT}
		>
			<div
				ref={stickyRef}
				className={cn(
					'hidden lg:block lg:sticky lg:top-(--site-header-offset-desktop) lg:z-2 bg-brand-black pt-5',
					'border-b border-b-neutral-light-100 transition-[border-color] duration-300',
				)}
			>
				{/* Most popular tag */}
				{yearly ? (
					<div className={cn('hidden lg:grid bg-brand-black', gridColsClassName)}>
						{/* empty leading cell */}
						<div />
						{plansData?.map((item) => (
							<div
								key={`${item?.id}-popular-header`}
								className={cn(
									'p-2 grid place-items-center',
									item.mostPopular &&
										'bg-neutral-light-100 text-brand-black rounded-t-lg lg:-ml-px lg:pl-px',
								)}
							>
								{item.mostPopular ? (
									<Paragraph fontSize="bodyBoldMedium" className="uppercase">
										{t('mostPopular')}
									</Paragraph>
								) : null}
							</div>
						))}
					</div>
				) : null}

				{/* Plan title row */}
				<div className={cn('grid', gridColsClassName)}>
					{/* empty leading cell */}
					<div />
					{plansData?.map(({ id, mostPopular, title }) => (
						<div
							key={`${id}-titles`}
							className={cn(
								'px-3.75 pt-7.5 lg:pt-5 pb-2.5 relative',
								mostPopular &&
									'pl-4 pr-3.75 bg-surface-featured lg:-ml-px border-x border-neutral-light-100',
							)}
						>
							<Heading tag="h2" className="mx-auto text-neutral-light-100" fontSize="headingMedium">
								{title}
							</Heading>
						</div>
					))}
				</div>

				{/* CTA's row */}
				<div className={cn('grid', gridColsClassName)}>
					{/* empty leading cell */}
					<div />
					{plansData?.map(({ id, button, mostPopular }) => (
						<PlanCallToAction
							key={`${id}-cta`}
							button={button}
							className={cn(
								'pb-4 lg:whitespace-nowrap',
								mostPopular && 'pl-4 pr-3.75 bg-surface-featured lg:-ml-px border-x',
							)}
						/>
					))}
				</div>
			</div>

			<div className="rounded-b-xl overflow-hidden min-w-0">
				{pricingSections.map((section) => (
					<Collapsible
						key={section.key}
						defaultOpen
						className="grid data-[state=closed]:border-b data-[state=closed]:border-neutral-700"
					>
						{/* Section header */}
						<CollapsibleTrigger className="group inline-flex items-center gap-2 cursor-pointer px-2 py-5 text-start min-w-0">
							<Heading tag="h3" fontSize="headingSmall" className="text-neutral-light-100 truncate">
								{tSections.rich(`${section.key}`, {
									mark: (chunks) => <span className="text-gradient">{chunks}</span>,
								})}
							</Heading>
							<ChevronDown className="size-5 ml-2 transition-transform duration-200 group-data-[state=open]:rotate-180" />
						</CollapsibleTrigger>

						<CollapsibleContent className="border border-neutral-700 rounded-xl">
							{/* Feature rows within the section */}
							{section.items.map((featureKey) => (
								<div key={`row-${section.key}-${featureKey}`} className={rowClassName}>
									<div
										className={cn(
											'px-4 py-5 flex justify-between items-center gap-1 text-neutral-light-100',
										)}
									>
										<div className="flex flex-col lg:flex-row gap-1 lg:items-center">
											<span className="lok-text-body-bold-medium">
												{tPlan.rich(`${featureKey}.label`, {
													mark: (chunks) => <span className="text-gradient">{chunks}</span>,
												})}
											</span>

											{tPlan.has(`${featureKey}.tag`) ? (
												<Tag
													name={tPlan(`${featureKey}.tag`)}
													size="small"
													className="w-fit mr-1"
													variant="gray-500"
												/>
											) : null}
										</div>

										{tPlan.has(`${featureKey}.tooltip`) ? (
											<Tooltip content={tPlan.raw(`${featureKey}.tooltip`)}>
												<span>
													<Info className="text-neutral-light-400 size-4 shrink-0" />
												</span>
											</Tooltip>
										) : null}
									</div>

									{plansData?.map((plan) => (
										<div
											key={`${section.key}-${plan?.id}-${featureKey}`}
											className={cn(
												'grid place-items-center px-4 py-5 text-neutral-light-100',
												plan.mostPopular && 'lg:bg-surface-featured',
											)}
										>
											<FeatureCell
												featureItem={plan?.features?.find(
													(item) => item.feature?.key === featureKey,
												)}
												yearly={yearly}
												defaultChecked={!!plan.allFeaturesIncluded}
											/>
										</div>
									))}
								</div>
							))}
						</CollapsibleContent>
					</Collapsible>
				))}
			</div>
		</div>
	)
}
